import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EnrollmentData {
  factorId: string;
  qrCode: string;
  secret: string;
}

interface UseMfaEnrollmentOptions {
  /** Called after successful verify (instead of resetting to idle step). */
  onVerifySuccess?: () => void;
  /** Called after refetch should be triggered (e.g. to update factor list). */
  onRefetch?: () => Promise<unknown>;
}

interface UseMfaEnrollmentReturn {
  enrollmentData: EnrollmentData | null;
  otpCode: string;
  setOtpCode: (code: string) => void;
  isSubmitting: boolean;
  isEnrolling: boolean;
  error: string | null;
  handleStartEnroll: () => Promise<boolean>;
  handleVerify: (code: string) => Promise<boolean>;
  handleCopySecret: () => void;
  reset: () => void;
}

/**
 * Shared MFA enrollment logic used by MfaEnrollmentCard and MfaEnrollmentScreen.
 *
 * - handleStartEnroll: cleans up unverified factors, calls supabase.auth.mfa.enroll.
 * - handleVerify: challenges + verifies a 6-digit TOTP code.
 * - handleCopySecret: copies the TOTP secret to clipboard.
 * - reset: clears all local state (useful for cancel/close).
 */
export function useMfaEnrollment(options: UseMfaEnrollmentOptions = {}): UseMfaEnrollmentReturn {
  const { t } = useTranslation();
  const { onVerifySuccess, onRefetch } = options;

  const [enrollmentData, setEnrollmentData] = useState<EnrollmentData | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // fallow-ignore-next-line complexity
  const handleStartEnroll = async (): Promise<boolean> => {
    setError(null);
    setIsEnrolling(true);
    try {
      // Clean up any existing unverified factors first
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      if (factorsData?.totp) {
        for (const factor of factorsData.totp) {
          if ((factor as { status: string }).status === "unverified") {
            await supabase.auth.mfa.unenroll({ factorId: factor.id });
          }
        }
      }

      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: "totp",
      });
      if (enrollError) throw enrollError;

      setEnrollmentData({
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
      });
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      const msg = message.includes("already exists") ? t("mfa.enrollConflictError") : message;
      setError(msg);
      return false;
    } finally {
      setIsEnrolling(false);
    }
  };

  // fallow-ignore-next-line complexity
  const handleVerify = async (code: string): Promise<boolean> => {
    if (code.length !== 6 || !enrollmentData) return false;
    setError(null);
    setIsSubmitting(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: enrollmentData.factorId,
      });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: enrollmentData.factorId,
        challengeId: challengeData.id,
        code,
      });
      if (verifyError) throw verifyError;

      toast.success(t("mfa.verifySuccess"));

      if (onVerifySuccess) {
        onVerifySuccess();
      } else {
        // Default: reset to idle (MfaEnrollmentCard behaviour)
        setEnrollmentData(null);
        setOtpCode("");
        if (onRefetch) await onRefetch();
      }
      return true;
    } catch {
      setError(t("mfa.verifyError"));
      setOtpCode("");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopySecret = (): void => {
    if (enrollmentData?.secret) {
      navigator.clipboard.writeText(enrollmentData.secret);
      toast.success(t("common.copied"));
    }
  };

  const reset = (): void => {
    setEnrollmentData(null);
    setOtpCode("");
    setError(null);
    setIsSubmitting(false);
    setIsEnrolling(false);
  };

  return {
    enrollmentData,
    otpCode,
    setOtpCode,
    isSubmitting,
    isEnrolling,
    error,
    handleStartEnroll,
    handleVerify,
    handleCopySecret,
    reset,
  };
}
